import React, { createContext, useContext, useState, useCallback, useRef, useEffect, ReactNode } from 'react';
import { apiFetch } from '../services/dataService';
import { startOfflineQueueWorker, flushQueue, pendingWritesSync, OFFLINE_QUEUE_EVENT } from '../utils/offlineQueue';
import { GOAL_CHANGED_EVENT } from '../utils/weightGoal';
import { refreshNutritionGoalsOnServer } from '../utils/nutritionGoalsStore';
import { useAuth } from './AuthContext';
import { prefetchActivePlan } from '../services/trainingService';
import { warmAppChunks } from '../utils/warmAppChunks';

/**
 * Safe localStorage readers used by the sync/backup payload builders.
 *
 * The backend `SyncData` model expects strict shapes (lists as lists, dicts
 * as dicts). If a localStorage key ever drifts from its expected shape (e.g.
 * a returning user with a legacy schema, mid-migration corruption, or a bug
 * that wrote the wrong type), a naive `JSON.parse(localStorage.getItem(k) ?? '[]')`
 * will silently ship the wrong shape to the server and the whole `/api/sync/upload`
 * request 422s — killing Auto Cloud Backup for that user.
 *
 * These helpers coerce anything unexpected back to the safe default so the
 * server accepts the payload and the corrupt key gets overwritten on the
 * next successful sync.
 */
function readLocalArray<T = any>(key: string): T[] {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch { return []; }
}
function readLocalObject<T extends object = Record<string, any>>(key: string): T {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return {} as T;
    const parsed = JSON.parse(raw);
    return (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) ? parsed : ({} as T);
  } catch { return {} as T; }
}
function readLocalObjectOrNull<T extends object = Record<string, any>>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) ? parsed : null;
  } catch { return null; }
}

// Sync status states
export const SYNC_STATUS = {
  IDLE: 'idle',
  SYNCING: 'syncing',
  SYNCED: 'synced',
  OFFLINE: 'offline',
  ERROR: 'error',
  BACKING_UP: 'backing_up',
  BACKED_UP: 'backed_up'
} as const;

export type SyncStatusType = typeof SYNC_STATUS[keyof typeof SYNC_STATUS];

interface BackupResult {
  success?: boolean;
  backup?: any;
  skipped?: boolean;
  reason?: string;
  error?: string;
}

interface SyncContextValue {
  status: SyncStatusType;
  lastSynced: Date | null;
  lastBackup: Date | null;
  pendingCount: number;
  errorMessage: string | null;
  startSync: () => void;
  completeSync: () => void;
  failSync: (error?: Error | null) => void;
  syncFetch: (url: string, options?: RequestInit) => Promise<Response>;
  createBackup: (silent?: boolean) => Promise<BackupResult>;
  isOnline: boolean;
  /** Writes made offline that are still waiting to reach the server. */
  queuedWrites: number;
  retryQueuedWrites: () => void;
}

interface SyncProviderProps {
  children: ReactNode;
}

const SyncContext = createContext<SyncContextValue | null>(null);

export function SyncProvider({ children }: SyncProviderProps) {
  const { user } = useAuth();
  const [status, setStatus] = useState<SyncStatusType>(SYNC_STATUS.IDLE);
  const [lastSynced, setLastSynced] = useState<Date | null>(null);
  const [lastBackup, setLastBackup] = useState<Date | null>(() => {
    try {
      const saved = localStorage.getItem('gym_notebook_last_backup');
      return saved ? new Date(saved) : null;
    } catch {
      return null;
    }
  });
  const [pendingCount, setPendingCount] = useState(0);
  const [queuedWrites, setQueuedWrites] = useState(pendingWritesSync);
  const [online, setOnline] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine));
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  
  const activeOps = useRef(0);
  const syncTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Guards against multiple concurrent createBackup() invocations. Without
  // this, a burst of `dataChanged` events (e.g. finishing a workout day +
  // adding a body measurement in quick succession) can each read + parse +
  // stringify the same 2-3 MB of localStorage on the main thread, freezing
  // the UI for several seconds on iPhone. iOS PWA freeze bug — production.
  const backupInFlight = useRef(false);
  const backupScheduled = useRef<ReturnType<typeof setTimeout> | null>(null);

  const startSync = useCallback(() => {
    activeOps.current += 1;
    setPendingCount(activeOps.current);
    setStatus(SYNC_STATUS.SYNCING);
    setErrorMessage(null);
    
    if (syncTimeout.current) {
      clearTimeout(syncTimeout.current);
    }
  }, []);

  const completeSync = useCallback(() => {
    activeOps.current = Math.max(0, activeOps.current - 1);
    setPendingCount(activeOps.current);
    
    if (activeOps.current === 0) {
      setStatus(SYNC_STATUS.SYNCED);
      setLastSynced(new Date());
      
      syncTimeout.current = setTimeout(() => {
        setStatus(SYNC_STATUS.IDLE);
      }, 3000);
    }
  }, []);

  const failSync = useCallback((error?: Error | null) => {
    activeOps.current = Math.max(0, activeOps.current - 1);
    setPendingCount(activeOps.current);
    
    if (activeOps.current === 0) {
      if (!navigator.onLine) {
        setStatus(SYNC_STATUS.OFFLINE);
        setErrorMessage('No internet connection');
      } else {
        setStatus(SYNC_STATUS.ERROR);
        setErrorMessage(error?.message || 'Sync failed');
      }
      
      syncTimeout.current = setTimeout(() => {
        setStatus(SYNC_STATUS.IDLE);
        setErrorMessage(null);
      }, 5000);
    }
  }, []);

  const createBackup = useCallback(async (silent = true): Promise<BackupResult> => {
    // Throttle: skip if a backup was made in the last 6 hours (4×/day cadence).
    // Previously gated to "same calendar day" which left a 24h window where
    // unsynced localStorage data could be lost if the user cleared history.
    if (lastBackup) {
      const ageMs = Date.now() - new Date(lastBackup).getTime();
      if (Number.isFinite(ageMs) && ageMs < 6 * 60 * 60 * 1000) {
        return { skipped: true, reason: 'Already backed up within last 6 hours' };
      }
    }

    // Concurrency guard: reject overlapping calls. Without this, a burst of
    // `dataChanged` events would each try to serialise the same MBs of
    // localStorage on the main thread, compounding the freeze.
    if (backupInFlight.current) {
      return { skipped: true, reason: 'Backup already in progress' };
    }
    backupInFlight.current = true;
    
    if (!navigator.onLine) {
      backupInFlight.current = false;
      if (!silent) {
        setStatus(SYNC_STATUS.OFFLINE);
      }
      return { error: 'No internet connection' };
    }
    
    try {
      setStatus(SYNC_STATUS.BACKING_UP);
      
      const trainingHistory = readLocalArray('gym_notebook_training_history');
      const exerciseHistory = readLocalObject('gym_notebook_exercise_history');
      const activePlan = readLocalObjectOrNull('gym_notebook_active_plan');
      const favorites = readLocalArray('gym_notebook_favorites');
      const bodyMeasurements = readLocalArray('gym_notebook_body_measurements');
      const customExercises = readLocalArray('gym_notebook_custom_exercises');
      const savedMeals = readLocalArray('gym_notebook_saved_meals');
      const nutritionDays = readLocalArray('gym_notebook_nutrition_days');
      const walkingSessions = readLocalArray('gym_notebook_walking_sessions');
      const customProducts = readLocalArray('gym_notebook_custom_products');
      
      await apiFetch('/api/sync/upload', {
        method: 'POST',
        body: JSON.stringify({
          data_type: 'full',
          training_plan: activePlan,
          training_history: trainingHistory,
          exercise_history: exerciseHistory,
          favorites: favorites,
          body_measurements: bodyMeasurements,
          progress_photos: [],
          settings: {},
          custom_exercises: customExercises,
          saved_meals: savedMeals,
          nutrition_days: nutritionDays,
          walking_sessions: walkingSessions,
          custom_products: customProducts,
        }),
      });
      
      const result = await apiFetch('/api/backup/create', { method: 'POST' });
      
      const now = new Date();
      setLastBackup(now);
      localStorage.setItem('gym_notebook_last_backup', now.toISOString());
      
      setStatus(SYNC_STATUS.BACKED_UP);
      
      syncTimeout.current = setTimeout(() => {
        setStatus(SYNC_STATUS.IDLE);
      }, 3000);
      
      return { success: true, backup: result };
    } catch (error: any) {
      console.error('Backup failed:', error);
      if (!silent) {
        setStatus(SYNC_STATUS.ERROR);
        setErrorMessage('Backup failed');
        syncTimeout.current = setTimeout(() => {
          setStatus(SYNC_STATUS.IDLE);
          setErrorMessage(null);
        }, 5000);
      } else {
        setStatus(SYNC_STATUS.IDLE);
      }
      return { error: error.message };
    } finally {
      backupInFlight.current = false;
    }
  }, [lastBackup]);

  const syncFetch = useCallback(async (url: string, options: RequestInit = {}): Promise<Response> => {
    startSync();
    try {
      const response = await fetch(url, options);
      if (response.ok) {
        completeSync();
      } else {
        failSync(new Error(`HTTP ${response.status}`));
      }
      return response;
    } catch (error: any) {
      failSync(error);
      throw error;
    }
  }, [startSync, completeSync, failSync]);

  useEffect(() => {
    const handleOnline = () => {
      setOnline(true);
      if (status === SYNC_STATUS.OFFLINE) {
        setStatus(SYNC_STATUS.IDLE);
        setErrorMessage(null);
      }
    };
    
    const handleOffline = () => {
      setOnline(false);
      setStatus(SYNC_STATUS.OFFLINE);
      setErrorMessage('No internet connection');
    };
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    if (!navigator.onLine) {
      setStatus(SYNC_STATUS.OFFLINE);
    }
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if (syncTimeout.current) {
        clearTimeout(syncTimeout.current);
      }
    };
  }, [status]);

  // Backup-on-write: any module dispatching `dataChanged` triggers a silent
  // backup. The 6-hour throttle inside createBackup() makes this cheap to call
  // on every write — only the first write after the 6h window actually hits S3.
  // This guards against mobile-data sessions where the timer-based auto-backup
  // never fires because the page is backgrounded before its setTimeout runs.
  //
  // PRODUCTION FIX (iOS PWA freeze): defer the actual backup work via
  // requestIdleCallback so the JSON.parse of MBs of localStorage + the
  // JSON.stringify of the upload body never runs on the same task as the
  // user's set-completion tap. Debounce coalesces bursts (finish workout day
  // + save body measurement + walking sync fire near-simultaneously).
  useEffect(() => {
    const scheduleBackup = () => {
      if (!navigator.onLine) return;
      if (backupScheduled.current) return; // already queued
      const run = () => {
        backupScheduled.current = null;
        void createBackup(true).catch(() => {});
      };
      const w = window as any;
      if (typeof w.requestIdleCallback === 'function') {
        backupScheduled.current = w.requestIdleCallback(run, { timeout: 8000 }) as any;
      } else {
        // Fallback: 500ms after the tap so the tap's rendering completes.
        backupScheduled.current = setTimeout(run, 500);
      }
    };
    window.addEventListener('dataChanged', scheduleBackup);
    return () => {
      window.removeEventListener('dataChanged', scheduleBackup);
      if (backupScheduled.current) {
        const w = window as any;
        if (typeof w.cancelIdleCallback === 'function') {
          try { w.cancelIdleCallback(backupScheduled.current); } catch { /* noop */ }
        } else {
          clearTimeout(backupScheduled.current);
        }
        backupScheduled.current = null;
      }
    };
  }, [createBackup]);

  // iter354 — offline write queue: replay parked writes on reconnect and keep
  // the "waiting to sync" count in sync with IndexedDB.
  useEffect(() => {
    const onQueueChanged = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      setQueuedWrites(typeof detail?.pending === 'number' ? detail.pending : pendingWritesSync());
    };
    window.addEventListener(OFFLINE_QUEUE_EVENT, onQueueChanged);
    const stop = startOfflineQueueWorker();
    return () => {
      window.removeEventListener(OFFLINE_QUEUE_EVENT, onQueueChanged);
      stop();
    };
  }, []);

  // iter354 — a weight-goal change must reach the server copy of the macro
  // goals even when the Nutrition page is not mounted; the trainer dashboard
  // and the AI Analyzer read that copy.
  useEffect(() => {
    const onGoalChanged = () => { void refreshNutritionGoalsOnServer(); };
    window.addEventListener(GOAL_CHANGED_EVENT, onGoalChanged);
    return () => window.removeEventListener(GOAL_CHANGED_EVENT, onGoalChanged);
  }, []);

  // iter356 — a session that expired while offline keeps its queued writes;
  // replay them as soon as the user is authenticated again. Also warm the
  // offline plan cache so Workout opens without a signal (iter357).
  useEffect(() => {
    if (!user) return;
    void flushQueue();
    void prefetchActivePlan();
    // Download the route chunks now, so the screens still open with no signal.
    warmAppChunks();
    // The count is per-account, so refresh it when the account changes.
    setQueuedWrites(pendingWritesSync());
  }, [user]);

  const retryQueuedWrites = useCallback(() => { void flushQueue(); }, []);

  const value: SyncContextValue = {
    status,
    lastSynced,
    lastBackup,
    pendingCount,
    errorMessage,
    startSync,
    completeSync,
    failSync,
    syncFetch,
    createBackup,
    isOnline: online,
    queuedWrites,
    retryQueuedWrites,
  };

  return (
    <SyncContext.Provider value={value}>
      {children}
    </SyncContext.Provider>
  );
}

export function useSync(): SyncContextValue {
  const context = useContext(SyncContext);
  if (!context) {
    throw new Error('useSync must be used within a SyncProvider');
  }
  return context;
}

export default SyncContext;
